/**
 * A „VALÓDI KONTÍR" — a Tensoftból behúzott könyvelés a bizonylat mellett
 * (#54, `design/t19-ketes-kotes/`, 2. változat).
 *
 * A DÖNTÉS HASÁBJÁBAN ÁLL, a jelöltek alatt és a Tételsorok fölött (#68, a
 * „B" elrendezés). A Tételsorok a könyvelő jegyzete; ez az, amit a
 * Tensoftban ténylegesen lekönyveltek.
 *
 * MINDEN ÁLLAPOT EGY MONDATOT KAP. Egy üres panel nem mondja meg, hogy a
 * számla nincs a Tensoftban, hogy a bizonylaton hiányzik a kulcs, vagy hogy
 * a könyvelő a sorokat egy másik számlának ítélte — és a három teendő más.
 *
 * ⚠️ A KÉTES KÖTÉSNÉL NINCS ELŐVÁLASZTÁS (T19). Az összeg egyezése csak
 * tájékoztat („= a bizonylat bruttója"), egyik gomb sem áll rá magától:
 * a csendes rossz kötés a fő ellenség, a döntés a könyvelőé.
 */

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  formatAmount,
  formatDate,
  formatDateTime,
  formatNumber,
} from '@/i18n/generated';
import { canRechoose, KEYED_CANDIDATES } from './chooser';
import type {
  MissingKeyField,
  TensoftInvoiceSummary,
  TensoftLedgerDecision,
  TensoftLedgerLine,
} from './types';
import type { TensoftBookingState } from './useTensoftBooking';

interface TensoftBookingProps {
  state: TensoftBookingState;
  /** A bizonylat bruttója és pénzneme — csak a jelöltek melletti tájékoztatáshoz. */
  documentGross: string | null;
  documentCurrency: string | null;
}

export function TensoftBooking(props: TensoftBookingProps) {
  const { state } = props;
  const { t } = useTranslation();

  return (
    <section className="real-booking" aria-label={t('worklist.tensoft.heading')}>
      <header className="real-booking__head">
        <span className="real-booking__label">{t('worklist.tensoft.heading')}</span>
        <span className="real-booking__source">{t('worklist.tensoft.source')}</span>
      </header>
      <NameMatchNotice view={state.view} />
      <Body {...props} />
      {state.error === null ? null : (
        <p className="error" role="alert">
          {state.error === 'conflict'
            ? t('worklist.tensoft.conflict')
            : t('worklist.tensoft.saveFailed')}
        </p>
      )}
    </section>
  );
}

/**
 * NÉV SZERINT TALÁLTUK, NEM ADÓSZÁM SZERINT (#79). EU-n kívüli szállítónál
 * sem a papíron, sem a KK-ban nincs adószám — a találat valószínű, nem
 * biztos. Kimondjuk, hogy a könyvelő ítélje meg.
 */
function NameMatchNotice({ view }: { view: TensoftBookingState['view'] }) {
  const { t } = useTranslation();
  if (view === null || !('matchedBy' in view) || view.matchedBy !== 'NAME') return null;
  return (
    <Message
      tone="attention"
      lead={t('worklist.tensoft.nameMatchLead')}
      body={t('worklist.tensoft.nameMatchBody')}
    />
  );
}

function Body(props: TensoftBookingProps) {
  const { state } = props;
  const { t } = useTranslation();
  const view = state.view;

  if (state.status === 'failed') {
    return (
      <p className="error">
        {t('worklist.tensoft.failed')}{' '}
        <button type="button" onClick={state.reload}>
          {t('worklist.retry')}
        </button>
      </p>
    );
  }
  if (view === null) {
    return <p className="hint">{t('worklist.tensoft.loading')}</p>;
  }
  if (state.chooser !== null) {
    return <Choice {...props} />;
  }

  switch (view.state) {
    case 'INCOMPLETE_KEY':
      return (
        <Message
          lead={t('worklist.tensoft.incompleteLead', { fields: missingList(t, view.missing) })}
          body={t('worklist.tensoft.incompleteBody')}
        />
      );
    case 'NOT_IN_TENSOFT':
      return (
        <Message
          lead={t('worklist.tensoft.notInTensoftLead')}
          body={t('worklist.tensoft.notInTensoftBody')}
        />
      );
    case 'AMBIGUOUS_INVOICE':
      return (
        <>
          <Message
            tone="attention"
            lead={t('worklist.tensoft.ambiguousLead', { count: view.candidates.length })}
            body={t('worklist.tensoft.ambiguousBody')}
          />
          <ul className="real-booking__invoices">
            {view.candidates.map((invoice) => (
              <li key={invoice.id}>
                <span className="real-booking__num">{invoice.tensoftNumber}</span>
                <span>{invoice.partnerName ?? '—'}</span>
                <span className="real-booking__num">{gross(t, invoice)}</span>
              </li>
            ))}
          </ul>
        </>
      );
    case 'BOOKED':
      return (
        <>
          {view.lines.length === 0 ? (
            <Message tone="official" lead={t('worklist.tensoft.notBookedLead')} />
          ) : (
            <div className="real-booking__booked">
              <div className="real-booking__booked-head">
                <b>{view.invoice.partnerName ?? view.invoice.invoiceNumber}</b>
                <BookedOn lines={view.lines} />
              </div>
              <Lines lines={view.lines} />
            </div>
          )}
          {view.decision === null ? null : (
            <Provenance state={state} decision={view.decision} verb="chosen" />
          )}
        </>
      );
    case 'BOOKED_ELSEWHERE':
      return (
        <>
          <Message
            lead={t('worklist.tensoft.elsewhereLead')}
            body={t('worklist.tensoft.elsewhereBody', {
              name: view.owner.partnerName ?? view.owner.invoiceNumber,
              date: view.owner.issueDate === null ? '—' : formatDate(view.owner.issueDate),
            })}
          />
          <Provenance state={state} decision={view.decision} verb="decided" />
        </>
      );
    case 'CHOSEN_NONE':
      return (
        <>
          <Message lead={t('worklist.tensoft.noneLead')} body={t('worklist.tensoft.noneBody')} />
          <Provenance state={state} decision={view.decision} verb="decided" />
        </>
      );
    case 'NEEDS_CHOICE':
      // A választó fent már visszatért; ide csak a típus kedvéért érünk.
      return null;
  }
}

/**
 * A VÁLASZTÓ — a sorok egyszer, alattuk a jelöltek (2. változat, 10-05).
 *
 * KÖZÖS SORLISTA, mert a Tensoft-adatban a sorokon nincs számlakulcs: nem
 * választhatók szét, tehát a könyvelő a SZÁMLÁT választja, nem a sorokat.
 */
function Choice({ state, documentGross, documentCurrency }: TensoftBookingProps) {
  const { t } = useTranslation();
  const chooser = state.chooser;
  if (chooser === null) return null;

  return (
    <>
      <Message
        tone="attention"
        lead={t('worklist.tensoft.needsChoiceLead', { count: chooser.candidates.length })}
        body={t('worklist.tensoft.needsChoiceBody', { number: chooser.invoice.tensoftNumber })}
      />

      <div className="real-booking__common">
        <div className="real-booking__common-head">
          <b>{t('worklist.tensoft.linesHeading', { number: chooser.invoice.tensoftNumber })}</b>
          <span className="real-booking__aside">
            {t('worklist.tensoft.lineCount', { count: chooser.lines.length })}
          </span>
        </div>
        {chooser.lines.length === 0 ? (
          <p className="hint real-booking__no-lines">{t('worklist.tensoft.noLines')}</p>
        ) : (
          <Lines lines={chooser.lines} />
        )}
      </div>

      <p className="real-booking__question">{t('worklist.tensoft.question')}</p>

      <ul className="real-booking__picks">
        {chooser.candidates.map((candidate, i) => (
          <Pick
            key={candidate.id}
            candidate={candidate}
            keyLabel={i < KEYED_CANDIDATES ? String(i + 1) : null}
            isDocument={candidate.id === chooser.invoice.id}
            current={candidate.id === chooser.currentOwnerId}
            documentGross={documentGross}
            documentCurrency={documentCurrency}
            state={state}
          />
        ))}
      </ul>

      <div className="real-booking__none">
        <span>{t('worklist.tensoft.noneLabel')}</span>
        <button
          type="button"
          className={
            chooser.currentOwnerId === null
              ? 'real-booking__choice real-booking__choice--current'
              : 'real-booking__choice'
          }
          disabled={state.saving}
          onClick={() => state.choose(null)}
        >
          <span className="real-booking__key" aria-hidden="true">
            0
          </span>
          {t('worklist.tensoft.none')}
        </button>
      </div>

      {state.rechoosing ? (
        <p>
          <button type="button" className="real-booking__choice" onClick={state.cancelRechoose}>
            {t('worklist.tensoft.cancel')}
          </button>
        </p>
      ) : null}
    </>
  );
}

function Pick({
  candidate,
  keyLabel,
  isDocument,
  current,
  documentGross,
  documentCurrency,
  state,
}: {
  candidate: TensoftInvoiceSummary;
  keyLabel: string | null;
  isDocument: boolean;
  current: boolean;
  documentGross: string | null;
  documentCurrency: string | null;
  state: TensoftBookingState;
}) {
  const { t } = useTranslation();
  const name = candidate.partnerName ?? candidate.invoiceNumber;
  const meta = [
    candidate.partnerTaxNumber,
    candidate.issueDate === null
      ? null
      : t('worklist.tensoft.issued', { date: formatDate(candidate.issueDate) }),
    isDocument ? t('worklist.tensoft.thisDocument') : null,
  ].filter((part) => part !== null);

  // TÁJÉKOZTATÁS, NEM AJÁNLÁS. Számként vetjük össze, mert a két oldal más
  // alakban jön (`45720` a tükörből, `45720.00` a bizonylatról) — és csak
  // azonos pénznemben: egy 45 720 EUR-s jelölt nem „egyezik" egy 45 720
  // forintos bizonylattal (review, 10-05). A hiányzó pénznem forint.
  const matches =
    candidate.grossAmount !== null &&
    documentGross !== null &&
    (candidate.currency ?? 'HUF') === (documentCurrency ?? 'HUF') &&
    Number(candidate.grossAmount) === Number(documentGross);

  return (
    <li
      className={current ? 'real-booking__pick real-booking__pick--current' : 'real-booking__pick'}
    >
      <span className="real-booking__key" aria-hidden="true">
        {keyLabel ?? ''}
      </span>
      <span className="real-booking__pick-meta">
        <b>{name}</b>
        {/* A RÉSZEK NEM TÖRNEK SZÉT: egy „2026-01-" / „31" sortörés a keltet
            olvashatatlanná tenné. A sor a részek KÖZÖTT tör. */}
        <span className="real-booking__pick-sub">
          {meta.map((part, i) => (
            <Fragment key={part}>
              {i > 0 ? ' · ' : ''}
              <span className="real-booking__nowrap">{part}</span>
            </Fragment>
          ))}
        </span>
      </span>
      <span className="real-booking__pick-gross">
        <span className="real-booking__num">{gross(t, candidate)}</span>
        {documentGross === null || candidate.grossAmount === null ? null : (
          <span
            className={
              matches ? 'real-booking__match real-booking__match--eq' : 'real-booking__match'
            }
          >
            {matches ? t('worklist.tensoft.grossMatches') : t('worklist.tensoft.grossDiffers')}
          </span>
        )}
      </span>
      <button
        type="button"
        className="real-booking__choice"
        aria-label={t('worklist.tensoft.pickFor', { name })}
        disabled={state.saving}
        onClick={() => state.choose(candidate.id)}
      >
        {t('worklist.tensoft.pick')}
      </button>
    </li>
  );
}

function Lines({ lines }: { lines: TensoftLedgerLine[] }) {
  const { t } = useTranslation();
  return (
    <table className="real-booking__lines">
      <thead>
        <tr>
          <th scope="col" className="real-booking__th">
            {t('worklist.tensoft.account')}
          </th>
          <th scope="col" className="real-booking__th real-booking__amount">
            {t('worklist.tensoft.debit')}
          </th>
          <th scope="col" className="real-booking__th real-booking__amount">
            {t('worklist.tensoft.credit')}
          </th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line, i) => (
          // A sorok sorrendje a Tensofté (`position`), és nincs saját azonosítójuk.
          // biome-ignore lint/suspicious/noArrayIndexKey: a sorrend maga az azonosító
          <tr key={i}>
            <td className="real-booking__td real-booking__num">{line.accountCode}</td>
            <td className="real-booking__td real-booking__amount real-booking__num">
              {amount(line.debit)}
            </td>
            <td className="real-booking__td real-booking__amount real-booking__num">
              {amount(line.credit)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BookedOn({ lines }: { lines: TensoftLedgerLine[] }) {
  const { t } = useTranslation();
  const date = lines.find((line) => line.bookingDate !== null)?.bookingDate ?? null;
  if (date === null) return null;
  return (
    <span className="real-booking__aside">
      {t('worklist.tensoft.bookedOn', { date: formatDate(date) })}
    </span>
  );
}

/** A DÖNTÉS NYOMA — ki, mikor —, és az újraválasztás bejárata. */
function Provenance({
  state,
  decision,
  verb,
}: {
  state: TensoftBookingState;
  decision: TensoftLedgerDecision;
  verb: 'chosen' | 'decided';
}) {
  const { t } = useTranslation();
  const at = formatDateTime(decision.chosenAt);
  const text =
    decision.chosenBy === null
      ? t(verb === 'chosen' ? 'worklist.tensoft.chosenAt' : 'worklist.tensoft.decidedAt', { at })
      : t(verb === 'chosen' ? 'worklist.tensoft.chosenBy' : 'worklist.tensoft.decidedBy', {
          name: decision.chosenBy,
          at,
        });

  return (
    <p className="real-booking__provenance">
      <span>{text}</span>
      {canRechoose(state.view) ? (
        <button type="button" className="real-booking__rechoose" onClick={state.startRechoose}>
          {t('worklist.tensoft.rechoose')}
        </button>
      ) : null}
    </p>
  );
}

function Message({
  lead,
  body,
  tone,
}: {
  lead: string;
  body?: string;
  tone?: 'attention' | 'official';
}) {
  return (
    <div className={tone ? `real-booking__msg real-booking__msg--${tone}` : 'real-booking__msg'}>
      <b>{lead}</b>
      {body === undefined ? null : <p>{body}</p>}
    </div>
  );
}

function missingList(t: (key: string) => string, missing: MissingKeyField[]): string {
  const names = missing.map((field) => t(`worklist.tensoft.fields.${field}`));
  return new Intl.ListFormat(DEFAULT_LOCALE, { type: 'conjunction' }).format(names);
}

/** A főkönyvi sor forintban áll — pénznemjel nélkül, ahogy a Tensoft mutatja. */
function amount(value: string | null): string {
  return value === null ? '' : formatNumber(Number(value));
}

/**
 * A bruttó a számla devizájában; devizás számlánál mellette a KK forintja
 * (#99) — a kontírsorok forintban állnak, így ez köti össze a kettőt, és
 * egy rosszul beírt árfolyam (mérve: 3,11 és 215 Ft/EUR) is látszik.
 */
function gross(
  t: (key: string, options?: Record<string, unknown>) => string,
  invoice: TensoftInvoiceSummary
): string {
  if (invoice.grossAmount === null) return '—';
  const currency = invoice.currency ?? 'HUF';
  const own = formatAmount(Number(invoice.grossAmount), { currency });
  if (currency === 'HUF' || invoice.grossAmountHuf === null) return own;
  return t('worklist.tensoft.grossInHuf', {
    gross: own,
    huf: formatAmount(Number(invoice.grossAmountHuf), { currency: 'HUF' }),
  });
}
